<template>
  <div class="p-4">
    <Toast />
    <Message v-if="store.error" severity="error" :closable="true" @close="store.clearError">{{ store.error }}
    </Message>

    <div>
      <div class="flex justify-between items-center mb-4">
        <div class="flex  items-center mb-4">
          <BackButton to="/settings"/>
          <div class="flex items-center">
            <h2 class="text-xl font-semibold">Tipos de Relacionamento</h2>
          </div>
        </div>
        <Button @click="openAddDialog" label="Adicionar Novo">
            <template #icon>
                <span class="material-icons mr-2">add</span>
            </template>
        </Button>
      </div>
      <RelationshipTypesTable :relationship-types="store.relationshipTypes" :is-loading="store.isLoading"
        :is-predefined-tab="false" @edit="openEditDialog" @delete="openDeleteDialog" />
    </div>

    <Dialog v-if="isDialogVisible && riskModelOptions" v-model:visible="isDialogVisible" :header="dialogHeader" modal
      class="w-full max-w-lg" :draggable="false">
      <RelationshipTypeForm :initial-data="selectedRelationshipType" :config-types="availableConfigTypes"
        :owned-types="ownedTypes" :options="riskModelOptions" @submit="handleFormSubmit" @cancel="closeDialog" />
    </Dialog>

    <ConfirmDialog :draggable="false" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRelationshipTypesStore } from '@/stores/relationshipTypes'
import RelationshipTypesTable from '@/components/accountAdmin/relationship-types/RelationshipTypesTable.vue'
import RelationshipTypeForm from '@/components/accountAdmin/relationship-types/RelationshipTypeForm.vue'
import Dialog from 'primevue/dialog'
import ConfirmDialog from 'primevue/confirmdialog'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import Message from 'primevue/message'
import Button from 'primevue/button'
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useRiskProfileOptions } from '@/composables/useRiskProfileOptions';
import BackButton from '@/components/base/BackButton.vue';

const store = useRelationshipTypesStore()
const { confirmDelete } = useConfirmDialog()
const toast = useToast()
const { options: riskModelOptions } = useRiskProfileOptions();

const isDialogVisible = ref(false)
const isEditMode = ref(false)
const selectedRelationshipType = ref({})

const dialogHeader = computed(() => (isEditMode.value ? 'Editar Tipo de Relacionamento' : 'Adicionar Novo Tipo de Relacionamento'))

// Config types not yet adopted into the account (adopting one twice would hit the unique index).
// Keyed off base_type.id since the response surfaces the adopted config via base_type, not a raw id.
const adoptedConfigIds = computed(
  () => new Set(store.relationshipTypes.filter((rt) => rt.base_type).map((rt) => rt.base_type.id))
)
const availableConfigTypes = computed(() =>
  store.configRelationshipTypes.filter((c) => c.active && !adoptedConfigIds.value.has(c.id))
)
// The account's own active types, offered as parents for a specialization.
const ownedTypes = computed(() => store.relationshipTypes.filter((rt) => rt.active))

onMounted(() => {
  store.fetchRelationshipTypes()
  store.fetchConfigRelationshipTypes()
})

const openAddDialog = () => {
  isEditMode.value = false
  selectedRelationshipType.value = { name: '', description: '', active: true, config_type_id: null, entity_types: [] }
  isDialogVisible.value = true
}

const openEditDialog = (relationshipType) => {
  isEditMode.value = true
  selectedRelationshipType.value = { ...relationshipType }
  isDialogVisible.value = true
}

const closeDialog = () => {
  isDialogVisible.value = false
  selectedRelationshipType.value = {}
}

const handleFormSubmit = async (formData) => {
  let success = false;
  if (isEditMode.value) {
    success = await store.updateRelationshipType(selectedRelationshipType.value.slug, formData);
  } else {
    success = await store.addRelationshipType(formData);
  }

  if (success) {
    closeDialog();
  } else {
    toast.add({
      severity: 'error',
      summary: 'Erro ao Salvar',
      detail: store.error,
      life: 3000
    });
  }
}

const openDeleteDialog = (relationshipType) => {
  confirmDelete({
    message: 'Tem certeza de que deseja excluir este tipo de relacionamento?',
    header: 'Confirmar Exclusão',
    accept: async () => {
      await store.deleteRelationshipType(relationshipType.id)
    }
  })
}
</script>
